import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking/guest after check; false reserved
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    // Only call /me if we have a hint that a session exists. httpOnly cookies
    // can't be read from JS, so we use a non-sensitive localStorage flag set on
    // login/register and cleared on logout or any 401.
    const hasSession = typeof window !== "undefined" && window.localStorage.getItem("jcc_session") === "1";
    if (!hasSession) {
      setUser(null);
      setChecking(false);
      return;
    }
    try {
      const { data, status } = await api.get("/api/auth/me", {
        validateStatus: (s) => s < 500,
      });
      if (status === 200 && data?.user) {
        setUser(data.user);
      } else {
        setUser(null);
        if (typeof window !== "undefined") window.localStorage.removeItem("jcc_session");
      }
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/api/auth/login", { email, password });
      setUser(data.user);
      if (typeof window !== "undefined") window.localStorage.setItem("jcc_session", "1");
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/api/auth/register", payload);
      setUser(data.user);
      if (typeof window !== "undefined") window.localStorage.setItem("jcc_session", "1");
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/api/auth/logout"); } catch {}
    setUser(null);
    if (typeof window !== "undefined") window.localStorage.removeItem("jcc_session");
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
